/**
 * Signed asset URL resolution for Mapbox image sources.
 *
 * Why this exists: Mapbox `addSource({type:'image', url})` loads via Image()
 * with crossOrigin='anonymous' — that mode never sends cookies, so the TD-8
 * sl_refresh cookie path can't authenticate Mapbox image loads. Plain <img>
 * tags (IndicationPopup, SLD viewer) keep working via cookie; only Mapbox
 * image sources need the workaround.
 *
 * Resolution strategy: hand any /api/v2/assets/... URL to this module before
 * passing to Mapbox; we mint a 1-hour signed URL via POST /api/v2/assets/sign
 * (HMAC, no auth required to fetch). Signed URLs are cached in memory and
 * never persisted — Zustand state and D1 entity rows continue to hold the
 * raw asset URL, since signed URLs would expire and corrupt saved projects.
 */

import { apiFetch, API_BASE } from './apiFetch';

const ASSET_PATH_MARKER = '/api/v2/assets/';
const SIGN_BATCH_LIMIT = 50; // server enforces this in handleSignAssets
const REFRESH_BEFORE_EXPIRY_SEC = 300; // refresh 5 min before real expiry

interface CacheEntry {
  signed: string;
  expiresAt: number; // unix seconds
}

const cache = new Map<string, CacheEntry>();

export function isAssetUrl(url: string): boolean {
  if (!url) return false;
  if (url.startsWith('blob:') || url.startsWith('data:')) return false;
  if (!url.includes(ASSET_PATH_MARKER)) return false;
  if (url.includes('expires=')) return false;
  return true;
}

export function extractR2Key(url: string): string | null {
  const idx = url.indexOf(ASSET_PATH_MARKER);
  if (idx === -1) return null;
  const after = url.slice(idx + ASSET_PATH_MARKER.length);
  return after.split('?')[0];
}

function cachedSignedUrl(r2Key: string): string | null {
  const entry = cache.get(r2Key);
  if (!entry) return null;
  const nowSec = Math.floor(Date.now() / 1000);
  if (entry.expiresAt - REFRESH_BEFORE_EXPIRY_SEC <= nowSec) {
    cache.delete(r2Key);
    return null;
  }
  return entry.signed;
}

function rememberSignedUrl(r2Key: string, signed: string): void {
  const expiresMatch = /[?&]expires=(\d+)/.exec(signed);
  const expiresAt = expiresMatch ? parseInt(expiresMatch[1], 10) : 0;
  if (!expiresAt || isNaN(expiresAt)) return;
  cache.set(r2Key, { signed, expiresAt });
}

/**
 * Server returns signed URLs as absolute `https://api.soleadlab.com/...`. In
 * production that's required (renderer lives on `app.soleadlab.com`, hits the
 * API cross-origin via CORS). In `vite` / `electron:dev` the renderer is at
 * `localhost:5173` — *not* in the worker's CORS allowlist — so Mapbox image
 * sources (crossOrigin='anonymous') would fail the CORS check even on signed
 * URLs, and plain `<img>`/`<video>` tags would 401 because the SameSite=Lax
 * `sl_refresh` cookie is bound to localhost in dev (not api.soleadlab.com).
 * Stripping the absolute prefix turns them into relative paths that route
 * through the Vite proxy in `vite.config.ts`, sidestepping both problems.
 *
 * Exported so non-Mapbox callers (resolveImageSrc, buildAssetUrl) can reuse
 * the same dev-only relativization without duplicating the predicate.
 */
// BOTH schemes, and http is not hypothetical. The worker mints signed URLs
// from `new URL(request.url).origin` (uploads.ts), and `wrangler dev` reports
// the CONFIGURED ROUTE's hostname rather than localhost — with no TLS in front
// of it. So a local worker returns `http://api.soleadlab.com/...`, which the
// https-only prefix silently failed to strip: Mapbox then fetched the asset
// from PRODUCTION, was refused (CORS, and a signature production never issued),
// and the layer rendered as nothing at all. Matching only https made every
// Mapbox image source — plan proxies, orthophoto previews — invisible in local
// dev while looking perfectly correct in the store and the database.
const ABSOLUTE_API_PREFIXES = [
  'https://api.soleadlab.com',
  'http://api.soleadlab.com',
];

export function devRelativize(url: string): string {
  if (!url) return url;
  if (!import.meta.env.DEV) return url;
  for (const prefix of ABSOLUTE_API_PREFIXES) {
    if (url.startsWith(prefix)) return url.slice(prefix.length);
  }
  return url;
}

async function signKeysBatch(keys: string[]): Promise<Record<string, string>> {
  if (keys.length === 0) return {};
  const result = await apiFetch<{ urls: Record<string, string> }>(
    '/api/v2/assets/sign',
    { method: 'POST', body: JSON.stringify({ keys }) },
  );
  const urls = result.urls || {};
  for (const k of Object.keys(urls)) urls[k] = devRelativize(urls[k]);
  return urls;
}

/**
 * Resolve a list of URLs to signed equivalents where applicable.
 * Returns a Map keyed by the original URL. Non-asset URLs (blob:, tile URLs,
 * already-signed URLs) map to themselves. Asset URLs whose signing fails
 * also map to themselves so Mapbox surfaces the failure exactly as before
 * the fix — no silent black tiles.
 */
export async function resolveSignedUrls(urls: string[]): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  const keyToOriginalUrls = new Map<string, string[]>(); // key → urls that need it
  const keysToSign: string[] = [];

  for (const url of urls) {
    if (!isAssetUrl(url)) {
      out.set(url, url);
      continue;
    }
    const r2Key = extractR2Key(url);
    if (!r2Key) {
      out.set(url, url);
      continue;
    }
    const cached = cachedSignedUrl(r2Key);
    if (cached) {
      out.set(url, cached);
      continue;
    }
    if (!keyToOriginalUrls.has(r2Key)) {
      keyToOriginalUrls.set(r2Key, []);
      keysToSign.push(r2Key);
    }
    keyToOriginalUrls.get(r2Key)!.push(url);
  }

  if (keysToSign.length === 0) return out;

  // Slice into ≤50-key chunks (server cap), fire in parallel.
  const chunks: string[][] = [];
  for (let i = 0; i < keysToSign.length; i += SIGN_BATCH_LIMIT) {
    chunks.push(keysToSign.slice(i, i + SIGN_BATCH_LIMIT));
  }

  try {
    const responses = await Promise.all(chunks.map(signKeysBatch));
    const merged: Record<string, string> = {};
    for (const r of responses) Object.assign(merged, r);

    for (const r2Key of keysToSign) {
      const signed = merged[r2Key];
      const originals = keyToOriginalUrls.get(r2Key)!;
      if (signed) {
        rememberSignedUrl(r2Key, signed);
        for (const u of originals) out.set(u, signed);
      } else {
        // Server skipped this key (per-site access denied or bad key) — fall
        // back to original URL so the failure mode matches pre-fix.
        for (const u of originals) out.set(u, u);
      }
    }
  } catch (err) {
    console.warn('[signedAssetUrl] batch sign failed, falling back to raw URLs:', err);
    for (const r2Key of keysToSign) {
      const originals = keyToOriginalUrls.get(r2Key)!;
      for (const u of originals) out.set(u, u);
    }
  }

  return out;
}

/** Convenience for single-URL callers — internally still goes through the batch path. */
export async function resolveSignedUrl(url: string): Promise<string> {
  const map = await resolveSignedUrls([url]);
  return map.get(url) ?? url;
}

// Hint to the bundler that API_BASE is referenced (kept available for callers
// that may want to construct URLs against it without re-importing apiFetch).
export { API_BASE };
